import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import currentAuthUserQueryFn from '@/pages/auth/queries/current-auth-user-query';
import meQueryFn from '@/pages/auth/queries/me-query';
import GlobalLoading from '@/global-loading';

// Custom event name for token changes
const TOKEN_CHANGE_EVENT = 'auth-token-change';

// Subscribe to token changes (both storage events and custom events)
const subscribeToToken = (callback: () => void) => {
  window.addEventListener('storage', callback);
  window.addEventListener(TOKEN_CHANGE_EVENT, callback);
  return () => {
    window.removeEventListener('storage', callback);
    window.removeEventListener(TOKEN_CHANGE_EVENT, callback);
  };
};

const getToken = () => localStorage.getItem('access_token');

// Helper to set token and dispatch event
export const setAuthToken = (token: string) => {
  localStorage.setItem('access_token', token);
  window.dispatchEvent(new Event(TOKEN_CHANGE_EVENT));
};

// Helper to remove token and dispatch event
export const removeAuthToken = () => {
  localStorage.removeItem('access_token');
  window.dispatchEvent(new Event(TOKEN_CHANGE_EVENT));
};

export interface Permission {
  id: number;
  name: string;
}

export interface Role {
  id: number;
  name: string;
  display_name: string;
  permissions: Permission[];
}

export interface Team {
  id: number;
  name: string;
}

export interface Token {
  access_token: string;
  type: string;
  expiration: number;
}

export interface Impersonator {
  id: number;
  name: string;
  email: string;
}

export interface MeUser {
  id: number;
  name: string;
  email: string;
  role: Role;
  team: Team;
  token: Token;
  is_impersonating: boolean;
  impersonator: Impersonator | null;
}

export interface MeResponse {
  data: MeUser;
}

// Legacy interface for backward compatibility
export interface CurrentAuthUser {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
  role: string;
  profile?: { avatar_path?: string | null } | null;
}

export interface CurrentAuthUserResponse {
  data: CurrentAuthUser;
}

interface AuthContextType {
  user?: CurrentAuthUserResponse | null;
  meUser?: MeResponse | null;
  isAuthenticated: boolean;
  can: (permissions: string | string[]) => boolean;
  hasRole: (roles: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Use useSyncExternalStore to reactively track token changes
  const token = useSyncExternalStore(subscribeToToken, getToken, getToken);

  const { data: user, isLoading: isUserLoading } = useQuery<
    CurrentAuthUserResponse,
    Error
  >({
    queryFn: currentAuthUserQueryFn,
    queryKey: ['currentAuthUser'],
    retry: false,
    staleTime: 0,
    enabled: !!token,
  });

  // Check if user is a recruiter (not a candidate) - only fetch /me for recruiters
  const isRecruiter = user?.data?.role !== 'candidate';

  const { data: meUser, isLoading: isMeLoading } = useQuery<MeResponse, Error>({
    queryFn: meQueryFn,
    queryKey: ['me'],
    retry: false,
    staleTime: 0,
    // Only fetch /me for recruiters (non-candidates)
    enabled: !!token && !!user && isRecruiter,
  });

  const isAuthenticated = useMemo(
    () => !!token && Boolean(user),
    [token, user],
  );

  const can = useCallback(
    (permissions: string | string[]): boolean => {
      // Candidates don't have detailed permissions, return false
      if (!meUser?.data?.role?.permissions) return false;

      const permissionArray = Array.isArray(permissions)
        ? permissions
        : [permissions];

      // Allow if permissions array is empty
      if (permissionArray.length === 0) return true;

      const userPermissions = meUser.data.role.permissions;
      // Check if any of the required permissions is present in user's permissions
      return permissionArray.some((permission) =>
        userPermissions.some(
          (userPermission) => userPermission.name === permission,
        ),
      );
    },
    [meUser],
  );

  const hasRole = useCallback(
    (roles: string | string[]): boolean => {
      if (!meUser?.data?.role?.name) return false;

      const roleArray = Array.isArray(roles) ? roles : [roles];
      return roleArray.includes(meUser.data.role.name);
    },
    [meUser],
  );

  // Only wait for /me loading if user is a recruiter
  const isLoading = isUserLoading || (isRecruiter && isMeLoading);

  if (isLoading) {
    return <GlobalLoading />;
  }

  return (
    <AuthContext.Provider
      value={{ user, meUser, isAuthenticated, can, hasRole }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
